import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUserPlus, 
  faCheck, 
  faTimes, 
  faCheckDouble, 
  faUser,
  faCalendar,
  faMessage
} from '@fortawesome/free-solid-svg-icons';
import groupService from '../../api/groupService';
import '../../styles/group/JoinRequests.css';

const JoinRequests = ({ groupId, isAdmin, onRequestsUpdate }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingRequest, setProcessingRequest] = useState(null);
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // Fetch pending join requests
  useEffect(() => {
    if (!isAdmin) return;
    
    const fetchRequests = async () => {
      try {
        setLoading(true);
        const pendingRequests = await groupService.getPendingJoinRequests(groupId);
        setRequests(pendingRequests);
        setError(null);
      } catch (error) {
        console.error('Error fetching join requests:', error);
        setError('Please refresh the page to reload join requests');
        setRequests([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, [groupId, isAdmin]);

  // Handle single request approval
  const handleApproveRequest = async (requestId, adminNotes = '') => {
    try {
      setProcessingRequest(requestId);
      await groupService.approveJoinRequest(groupId, requestId, adminNotes);
      
      // Remove from requests list
      setRequests(prev => prev.filter(req => req.id !== requestId));
      
      // Update parent component
      if (onRequestsUpdate) {
        onRequestsUpdate();
      }
    } catch (error) {
      console.error('Error approving request:', error);
      setError('Failed to approve request');
    } finally {
      setProcessingRequest(null);
    }
  };

  // Handle single request denial
  const handleDenyRequest = async (requestId, adminNotes = '') => {
    try {
      setProcessingRequest(requestId);
      await groupService.denyJoinRequest(groupId, requestId, adminNotes);
      
      // Remove from requests list
      setRequests(prev => prev.filter(req => req.id !== requestId));
      
      // Update parent component
      if (onRequestsUpdate) {
        onRequestsUpdate();
      }
    } catch (error) {
      console.error('Error denying request:', error);
      setError('Failed to deny request');
    } finally {
      setProcessingRequest(null);
    }
  };

  // Handle bulk approval
  const handleApproveAll = async () => {
    try {
      setBulkProcessing(true);
      await groupService.approveAllJoinRequests(groupId);
      
      // Clear all requests
      setRequests([]);
      setSelectedRequests([]);
      
      // Update parent component
      if (onRequestsUpdate) {
        onRequestsUpdate();
      }
    } catch (error) {
      console.error('Error approving all requests:', error);
      setError('Failed to approve all requests');
    } finally {
      setBulkProcessing(false);
    }
  };

  // Handle checkbox selection
  const handleSelectRequest = (requestId) => {
    setSelectedRequests(prev => {
      if (prev.includes(requestId)) {
        return prev.filter(id => id !== requestId);
      } else {
        return [...prev, requestId];
      }
    });
  };

  // Handle select all checkbox
  const handleSelectAll = () => {
    if (selectedRequests.length === requests.length) {
      setSelectedRequests([]);
    } else {
      setSelectedRequests(requests.map(req => req.id));
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Don't render anything for non-admin users
  if (!isAdmin) return null;

  if (loading) {
    return (
      <div className="join-requests-loading">
        <div className="loading-spinner"></div>
        <p>Loading join requests...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="join-requests-error">
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="join-requests-empty">
        <div className="empty-state-icon">
          <FontAwesomeIcon icon={faUserPlus} size="3x" />
        </div>
        <h3>No Pending Requests</h3>
        <p>When users request to join your group, they'll appear here</p>
      </div>
    );
  }

  return (
    <div className="join-requests-container">
      <div className="join-requests-header">
        <div className="requests-info">
          <h3>
            <FontAwesomeIcon icon={faUserPlus} />
            Join Requests ({requests.length})
          </h3>
        </div>
        
        {requests.length > 0 && (
          <div className="bulk-actions">
            <label className="select-all-checkbox">
              <input
                type="checkbox"
                checked={selectedRequests.length === requests.length}
                onChange={handleSelectAll}
              />
              Select All ({requests.length})
            </label>
            
            <button
              className="approve-all-btn"
              onClick={handleApproveAll}
              disabled={bulkProcessing || requests.length === 0}
            >
              <FontAwesomeIcon icon={faCheckDouble} />
              {bulkProcessing ? 'Approving...' : `Approve All (${requests.length})`}
            </button>
          </div>
        )}
      </div>

      <div className="join-requests-list">
        {requests.map(request => (
          <div key={request.id} className="join-request-card">
            <div className="request-checkbox">
              <input
                type="checkbox"
                checked={selectedRequests.includes(request.id)}
                onChange={() => handleSelectRequest(request.id)}
              />
            </div>
            
            <div className="request-user-info">
              <div className="user-avatar">
                {request.user?.profile_image ? (
                  <img src={request.user.profile_image} alt={request.user.name} />
                ) : (
                  <FontAwesomeIcon icon={faUser} />
                )}
              </div>
              
              <div className="user-details">
                <h4 className="user-name">
                  {request.user?.first_name || request.user?.name || 'Unknown User'}
                  {request.user?.last_name && ` ${request.user.last_name}`}
                </h4>
                <p className="user-email">{request.user?.email}</p>
                
                <div className="request-meta">
                  <span className="request-date">
                    <FontAwesomeIcon icon={faCalendar} />
                    {formatDate(request.requested_at)}
                  </span>
                </div>
                
                {request.message && (
                  <div className="request-message">
                    <FontAwesomeIcon icon={faMessage} />
                    <span>{request.message}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="request-actions">
              <button
                className="approve-btn"
                onClick={() => handleApproveRequest(request.id)}
                disabled={processingRequest === request.id}
                title="Approve Request"
              >
                <FontAwesomeIcon icon={faCheck} />
                {processingRequest === request.id ? '...' : 'Approve'}
              </button>
              
              <button
                className="deny-btn"
                onClick={() => handleDenyRequest(request.id)}
                disabled={processingRequest === request.id}
                title="Deny Request"
              >
                <FontAwesomeIcon icon={faTimes} />
                {processingRequest === request.id ? '...' : 'Deny'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default JoinRequests;
